import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { AlreadyLoggedIn } from '../../components/auth-layout/AlreadyLoggedIn';
import { AuthLayout } from '../../components/auth-layout/AuthLayout';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { COINIS_LOGO_DARK } from '../../config';
import { authService } from '../../services/auth';
import { usePreexistingSession } from '../../utils/hooks/usePreexistingSession';
import { generateErrorMessages } from '../../utils/showApiErrors';
import { PasswordStrengthIndicator } from '../setup/components/PasswordStrengthIndicator';
import { usePasswordValidation } from '../setup/hooks/usePasswordValidation';
import { passwordSchema } from '../setup/schemas/password';
import { LinkError } from './components/LinkError';

const resetPasswordSchema = z.object({
  newPassword: passwordSchema.shape.password
});

type ResetFormValues = z.infer<typeof resetPasswordSchema>;

type ResetStep = 'reset-form' | 'complete';

export const ResetPassword = (): JSX.Element => {
  const [currentStep, setCurrentStep] = useState<ResetStep>('reset-form');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(200);
  const [passwordValue, setPasswordValue] = useState<string>('');
  const { passwordStrength, showPasswordStrength } = usePasswordValidation(passwordValue);
  const history = useHistory();
  const signedInElsewhere = usePreexistingSession();

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const urlToken = queryParams.get('token');

  const resetForm = useForm<ResetFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: ''
    }
  });

  // Check if we have a token in URL on component mount
  useEffect(() => {
    // A signed-in visitor sees <AlreadyLoggedIn /> instead of the reset form, so
    // hold off on validating the token: the request would carry the current
    // session's cookies, and a rejection would paint an "invalid link" screen
    // over the log-out prompt. This re-runs once they log out.
    if (signedInElsewhere) {
      return;
    }

    if (urlToken) {
      // The effect can now run more than once per mount (a log-out re-triggers
      // it), so drop the response of a superseded run instead of letting it
      // overwrite the current state.
      let cancelled = false;

      const validateToken = async () => {
        setIsLoading(true);
        try {
          await authService.validatePasswordResetToken({ token: urlToken });
        } catch (e) {
          const axiosError = e as AxiosError;
          if (!cancelled) {
            setErrorStatus(axiosError.response?.status);
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      validateToken();

      setCurrentStep('reset-form');

      return () => {
        cancelled = true;
      };
    }

    setErrorStatus(404);

    return undefined;
  }, [urlToken, signedInElsewhere]);

  const handleBackToLogin = () => {
    history.push('/login');
  };

  const handleResetSubmit = async (values: ResetFormValues) => {
    if (!urlToken) {
      setError('Reset token is missing. Please use the link from your email.');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await authService.resetPassword({
        token: urlToken,
        password: values.newPassword
      });
      setCurrentStep('complete');
    } catch (e) {
      const axiosError = e as AxiosError;
      setError(generateErrorMessages(axiosError));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />

      {/* Reset links are emailed and often opened in a browser that already
          holds a session for another account. Explain that instead of silently
          bouncing the visitor to the dashboard and dropping the token. */}
      {signedInElsewhere && (
        <AlreadyLoggedIn description="This reset link is for a different account than the one you're signed in to. Log out of your current session to continue — you'll come right back to this page." />
      )}

      {!signedInElsewhere && errorStatus === 400 && (
        <LinkError
          title="Reset link expired"
          description="This password reset link has expired for security reasons."
        />
      )}
      {!signedInElsewhere && errorStatus === 404 && (
        <LinkError
          title="Invalid reset link"
          description="This password reset link is no longer valid. It may have already been used or expired."
        />
      )}
      {!signedInElsewhere && errorStatus === 200 && currentStep === 'reset-form' && (
        <>
          <div className="flex flex-col gap-[11px]">
            <h1 className="font-onboarding-1 text-[2rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary min-[880px]:text-[2.35rem]">
              Create a new password
            </h1>
            <p className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">
              Choose a new, secure password for your Coinis account.
            </p>
          </div>

          <Form {...resetForm}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void resetForm.handleSubmit(handleResetSubmit)(e);
              }}
              className="flex flex-col gap-[18px]"
            >
              <FormField
                control={resetForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative flex items-center">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your new password"
                          className="pr-11"
                          aria-invalid={!!resetForm.formState.errors.newPassword}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setPasswordValue(e.target.value);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-[15px] flex text-[var(--ink-3)] transition hover:text-[var(--ink)]"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Error Display */}
              {error && (
                <div className="text-sm font-medium text-loss">
                  {Array.isArray(error) ? (
                    <div className="space-y-1">
                      {error.map((err, index) => (
                        <div key={index}>{err}</div>
                      ))}
                    </div>
                  ) : (
                    error
                  )}
                </div>
              )}

              <PasswordStrengthIndicator strength={passwordStrength} showStrength={showPasswordStrength} />

              <button
                type="submit"
                disabled={isLoading}
                className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                    <span>Updating Password...</span>
                  </div>
                ) : (
                  'Update Password'
                )}
              </button>
            </form>
          </Form>

          <p className="text-[14px] text-[var(--ink-2)]">
            Remembered it?{' '}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="font-semibold text-primary transition hover:underline"
            >
              Back to log in
            </button>
          </p>
        </>
      )}

      {!signedInElsewhere && errorStatus === 200 && currentStep === 'complete' && (
        <>
          <div className="flex flex-col gap-[11px]">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-onboarding-success-light text-onboarding-success">
              <CheckCircle className="h-7 w-7" />
            </span>
            <h1 className="font-onboarding-1 text-[2rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary min-[880px]:text-[2.35rem]">
              Password updated
            </h1>
            <p className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">
              Your password has been updated. You can now sign in with your new password.
            </p>
          </div>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue to log in
          </button>
        </>
      )}
    </AuthLayout>
  );
};
